import { Button } from "@/components/ui/button";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { AnimatedEffect } from "@components/NewGamePage/GPOnboarding/AnimatedEffect";
import Image from "next/image";
import { GlassButton } from "../GlassButton";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import type { GPOnboardingStepProps } from "./utils";

// progress values
const CURRENT_ENTRIES = 8363;
const GOAL_ENTRIES = 10000;
const PROGRESS = Math.min(CURRENT_ENTRIES / GOAL_ENTRIES, 1);

export const OnboardingExtrinsic = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const handleDontHaveSteamClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "get-started-no-steam",
        controlLabel: "Don't have Steam?",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    onNext();
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="/images/games/8ball/8ball-bg2.webp"
            alt=""
            fill
            className="object-cover"
            style={{
              objectPosition: "60% center",
            }}
          />
        </div>
        <div className="absolute inset-0 bg-black/80" />
      </div>

      {/* Imagery Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Winning Animation*/}
        <div className="pointer-events-none absolute left-1/2 -top-12 h-[14rem] w-[14rem] -translate-x-1/2 mix-blend-lighten opacity-15">
          <video
            className="h-full w-full object-cover object-center"
            src="/video/prize-wheel/spin-prize-overlay.mp4"
            muted
            loop
            playsInline
            preload="metadata"
            autoPlay
          />
        </div>

        {/* Header + Logo + Prize Cube  */}
        <div className="pointer-events-none absolute inset-x-0 top-[3rem] flex justify-center px-4 sm:top-18">
          <div className="relative aspect-[412/274] w-[85%] max-w-[25.75rem]">
            <Image
              src="/images/games/8ball/onboarding-header-extrinsic.webp"
              alt=""
              width={412}
              height={274}
              className="h-auto w-full object-cover object-center"
            />

            {/* Prize Cube */}
            <div className="absolute left-1/2 top-[64%] sm:top-[65%] h-[6.5rem] w-[6.5rem] sm:h-[7.5rem] sm:w-[7.5rem] -translate-x-1/2 -translate-y-1/2">
              <Image
                src="/images/games/8ball/onboarding-prize-cube-modified.webp"
                alt=""
                fill
                className="object-contain"
              />
            </div>

            {/* Logo  */}
            <div className="absolute left-[25%] top-[58%] sm:left-[25%] sm:top-[59%] h-[5.5rem] w-[12.5rem] max-w-[50%] shadow-2xl">
              <Image
                src="/images/games/8ball/logo.webp"
                alt="8 Ball by Pokerist"
                width={232}
                height={100}
                className="object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chips */}
      <div className="absolute bottom-0 left-0 w-full">
        <Image
          src="/images/games/8ball/onboarding-chips.webp"
          alt=""
          width={412}
          height={157}
          className="h-auto w-full object-contain object-bottom pointer-events-none"
        />
      </div>

      {/* UI Layer */}
      <div
        className={cn(
          "absolute inset-0 flex flex-col items-center justify-between",
          "bg-gradient-to-b from-transparent from-[75%] to-black/20",
          "px-5 pb-[clamp(2rem,8vh,7.5rem)]",
          "pt-[clamp(16rem,28vh,12rem)] sm:pt-[clamp(20rem,36vh,21rem)]",
        )}
      >
        {/* Title + Progress */}
        <div className="flex w-full flex-col items-center gap-6">
          <div className="flex w-full flex-col items-center gap-[clamp(1rem,4vh,1.5rem)]">
            <p className="text-center font-suisse text-[clamp(1.5rem,6vw,2rem)] font-bold leading-[clamp(2rem,6.5vw,2.5rem)] text-[#f7f7f7]">
              $60k in prizes
              <br />
              already unlocked!
            </p>
          </div>

          {/* Progress Bar Section */}
          <div className="flex w-full flex-col gap-4 px-2">
            <div className="flex h-[0.625rem] w-full items-center gap-1 px-3 sm:px-10">
              <div className="relative flex flex-1 items-center">
                <div
                  className={cn(
                    "relative h-2 w-full overflow-hidden rounded-[0.125rem] bg-white/20",
                    "glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0",
                  )}
                >
                  <div
                    className="h-full bg-gradient-to-r from-[#D37A38] to-[#F5A623] transition-all duration-500"
                    style={{ width: `${PROGRESS * 100}%` }}
                  />
                </div>

                {/* Prize Cube on Progress Bar */}
                <div
                  className="pointer-events-none absolute -top-2 h-8 w-8 -translate-x-1/2"
                  style={{ left: `${PROGRESS * 100}%` }}
                >
                  <Image
                    src="/images/games/8ball/onboarding-prize-cube.webp"
                    alt=""
                    fill
                    className="object-contain"
                  />
                </div>
              </div>

              {/* Progress Text */}
              <div className="flex shrink-0 flex-col justify-center text-center">
                <p className="whitespace-nowrap font-suisse text-xs font-medium leading-none text-[#f7f7f7]">
                  {CURRENT_ENTRIES.toLocaleString()} /{" "}
                  {GOAL_ENTRIES.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Unlock Text */}
            <p className="whitespace-pre-wrap text-center font-suisse text-sm sm:text-base font-medium leading-none text-[#f7f7f7]">
              $100k unlocks at 10,000 Steam wishlists
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex w-full flex-col items-center gap-[clamp(1.5rem,4vh,2rem)]">
          <div className="relative mt-[3.125rem] w-full max-w-[23.25rem]">
            {/* Animated Effect */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[clamp(8rem,20vh,12rem)] w-[clamp(16rem,40vw,20rem)] -translate-x-1/2 -translate-y-1/2">
              <AnimatedEffect className="animate-pulse" tint="#fcca74" />
            </div>

            <GlassButton
              onClick={() => onNext()}
              control="get-started"
              section={GamePageSection.ONBOARDING_MODAL}
              className="relative z-10 !h-[clamp(3.75rem,9vh,4.5rem)] w-full flex-col px-8 py-2.5"
              variant="cta"
            >
              <p className="text-base font-medium leading-6 text-foreground">
                Sign in with Steam to enter
              </p>
            </GlassButton>

            {/* Prize Cube Overlay on Button */}
            <div className="pointer-events-none absolute left-1/2 top-0 z-20 size-[clamp(3.75rem,18vw,4.6875rem)] -translate-x-1/2 -translate-y-3/4">
              <Image
                src="/images/games/8ball/onboarding-prize-cube.webp"
                alt=""
                fill
                className="object-contain"
              />
            </div>
          </div>

          {/* Don't have Steam Button */}
          <Button
            onClick={handleDontHaveSteamClick}
            variant="ghost"
            className="h-9 rounded-[1.375rem] bg-transparent px-4 py-2 text-sm text-foreground"
          >
            Don't have Steam?
          </Button>
        </div>

        {/* Rules & Privacy */}
        <div className="max-w-[20rem]">
          <TermsAndPrivacy track={track} />
        </div>
      </div>
    </div>
  );
};
